import { action, computed, flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { getStorage, removeStorage, saveLocalStorage } from '@shared/utils/browsers';
import { AuthApi, ProfileApi } from '@shared/apis';
import { ROUTES } from '@shared/configs/constants';
import { toastify } from '@shared/utils/toastify';
import { t } from 'i18next';
import { Profile } from '@shared/types/profile';
import { ResponseData } from '@shared/types/http';
import { LoginPostRequest, LoginPostResponse, PostConfirmPasswordRequest, PostForgotPasswordRequest } from '@shared/types/http-payload/auth';
import { ROLE } from '@shared/types/enums';

export default class AuthStore extends BaseStore {
    isLoggedIn: boolean = false;
    profile?: Profile;
    token: string = getStorage('token');
    api: AuthApi;
    profileApi: ProfileApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            isLoggedIn: observable,
            isStdIndividual: computed,
            isStdGroup: computed,
            isGroupAdmin: computed,
            profile: observable,
            token: observable,
            login: flow.bound,
            logout: action.bound,
            getUserByUUID: flow.bound,
            forgotPassword: flow.bound,
            getCheckUuidConfirmPassword: flow.bound,
            confirmPassword: flow.bound,
            registerUserInfo: flow.bound,
            registerUserByEmail: flow.bound,
            changePassword: flow.bound,
            clearAuthentication: action.bound,
            getProfile: flow.bound,
            verifyChangeEmail: flow.bound,
            updateUserProfile: flow.bound,
            loginSSO: flow.bound
        });
        this.api = new AuthApi();
        this.profileApi = new ProfileApi();

        if (this.token && window.location.pathname !== '/login-sso') {
            this.getProfile();
        }
    }

    *getProfile() {
        try {
            const res: ResponseData<Profile> = yield this.rootStore.apiStore.call(
                this.profileApi,
                this.profileApi.getUserProfile
            );
            if (res?.ok) {
                this.profile = res.data;
                this.isLoggedIn = true;
            }
        } catch (error) {
            console.warn('Error getProfile in AuthStore:', error);
        }
    }

    *login(payload: LoginPostRequest) {
        try {
            const res: ResponseData<LoginPostResponse> = yield this.rootStore.apiStore.call(this.api, this.api.login, payload);
            if (res?.ok) {
                saveLocalStorage('token', res.data.token);
                this.token = res.data.token;
                yield this.getProfile();
                // toastify('success', t('messages.sign_in_success'));
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *registerUserByEmail(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.registerEmail, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *getUserByUUID(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getUserByUUID, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *registerUserInfo(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.registerUserInfor, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *forgotPassword(body: PostForgotPasswordRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.forgotPassword, body);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.warn('Error forgotPassword in AuthStore:', error);
            return false;
        }
    }

    *getCheckUuidConfirmPassword(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getCheckUuidConfirmPassword, payload, { handleErrorBySelf: true });
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.warn('Error getCheckUuidConfirmPassword in AuthStore:', error);
            return false;
        }
    }

    *confirmPassword(body: PostConfirmPasswordRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.confirmPassword, body);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.warn('Error confirmPassword in AuthStore:', error);
            return false;
        }
    }

    *changePassword(payload: { currentPassword: string, newPassword: string, confirmPassword: string }) {
        try {
            const body = {
                currentPassword: payload.currentPassword,
                password: payload.newPassword,
                confirmPassword: payload.confirmPassword
            };

            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                () => this.api.changePassword(body)
            );

            if (res?.ok) {
                toastify('success', 'パスワードが変更されました');
                return true;
            }
            return false;
        } catch (error) {
            console.warn('Error changePassword in AuthStore:', error);
            return false;
        }
    }

    *verifyChangeEmail(payload: { token: string }) {
        try {
            const params = {
                token: payload.token
            };

            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.verifyChangeEmail, params, { handleErrorBySelf: true });

            if (res?.ok) {
                toastify('success', 'メールアドレスが変更されました');
                return true;
            }
            return false;
        } catch (error) {
            console.warn('Error verifyChangeEmail in AuthStore:', error);
            return false;
        }
    }

    *updateUserProfile(payload: any) {
        try {
            const res: ResponseData<Profile> = yield this.rootStore.apiStore.call(
                this.profileApi,
                () => this.profileApi.updateUserProfile(payload)
            );
            if (res?.ok) {
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error updateUserProfile in AuthStore:', error);
            return null;
        }
    }

    *loginSSO(payload: { ssoToken: string, orgId: string, service: string, screenType: string }) {
        try {
            const res: ResponseData<LoginPostResponse> = yield this.rootStore.apiStore.call(this.api, this.api.loginSSO, payload);
            if (res?.ok) {
                saveLocalStorage('token', res.data.token);
                this.token = res.data.token;
                yield this.getProfile();
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    logout() {
        try {
            this.rootStore.apiStore.call(this.api, this.api.logout);
            this.clearAuthentication();
        } catch (error) {
        }
    }

    get isStdIndividual() {
        return this.profile?.roleIds.includes(ROLE.STD_INDIVIDUAL);
    }

    get isStdGroup() {
        return this.profile?.roleIds.includes(ROLE.STD_GROUP);
    }

    get isGroupAdmin() {
        return this.profile?.roleIds.includes(ROLE.GROUP_ADMIN);
    }

    clearAuthentication() {
        this.isLoggedIn = false;
        localStorage.clear();
        localStorage.setItem('logout-event', Date.now().toString());
        window.location.href = ROUTES.home.href;
    }
}
